import React, { useEffect, useMemo, useState } from 'react';
import './Download.css';

const Download = () => {
  // Using existing api.planmysemester.xyz HTTPS - APK served from same server
  const APK_URL = 'https://api.planmysemester.xyz/downloads/Seven_Days_1768413594.apk';

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryVisibleCount, setGalleryVisibleCount] = useState(3);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setGalleryVisibleCount(mq.matches ? 1 : 3);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  const galleryItems = useMemo(() => {
    const imageNumbers = [1, 2, 3, 4, 5, 6, 7, 8];
    const images = imageNumbers.map((n) => ({
      type: 'image',
      src: `/images/Screenshots%207Days/${n}.png`,
      alt: `Screenshot ${n}`,
      imageIndex: n - 1,
    }));
    return [
      {
        type: 'video',
        src: 'https://www.youtube.com/embed/BRBP1sdgyTE',
        title: '7 Days Trailer',
      },
      ...images,
    ];
  }, []);

  const imageItems = useMemo(() => {
    return galleryItems.filter((i) => i.type === 'image');
  }, [galleryItems]);

  const galleryMaxIndex = Math.max(0, galleryItems.length - galleryVisibleCount);
  const canGoPrev = galleryIndex > 0;
  const canGoNext = galleryIndex < galleryMaxIndex;

  const goPrev = () => setGalleryIndex((i) => Math.max(0, i - 1));
  const goNext = () => setGalleryIndex((i) => Math.min(galleryMaxIndex, i + 1));

  useEffect(() => {
    setGalleryIndex((i) => Math.min(i, galleryMaxIndex));
  }, [galleryMaxIndex]);

  const openImageModal = (idx) => {
    setImageModalIndex(idx);
    setIsImageOpen(true);
  };

  const closeImageModal = () => setIsImageOpen(false);

  const canImagePrev = imageModalIndex > 0;
  const canImageNext = imageModalIndex < imageItems.length - 1;
  const imagePrev = () => setImageModalIndex((i) => Math.max(0, i - 1));
  const imageNext = () => setImageModalIndex((i) => Math.min(imageItems.length - 1, i + 1));

  useEffect(() => {
    if (!isImageOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsImageOpen(false);
      }
      if (e.key === 'ArrowLeft') {
        setImageModalIndex((i) => Math.max(0, i - 1));
      }
      if (e.key === 'ArrowRight') {
        setImageModalIndex((i) => Math.min(imageItems.length - 1, i + 1));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isImageOpen, imageItems.length]);

  return (
    <div className="download-page">
      {/* Hero Media Section with Overlay, matching Play Store layout */}
      <div className="hero-media">
        <img
          className="hero-art"
          src="/images/Screenshots%207Days/1.png"
          alt=""
          aria-hidden="true"
        />
        <div className="hero-overlay">
          <div className="hero-layout">
            <div className="hero-left-column">
              <div className="hero-text-block">
                <h1 className="hero-title">7 Days</h1>
                <div className="hero-developer-line">
                  <span className="hero-developer">Daver The Dave</span>
                </div>
              </div>

              <div className="hero-stats-row">
                <div className="hero-stat">
                  <div className="hero-stat-main">5.0<span className="star">★</span></div>
                  <div className="hero-stat-label">Reviews</div>
                </div>
                <div className="hero-stat-separator"></div>
                <div className="hero-stat">
                  <div className="hero-stat-main"><span className="age-badge">17+</span></div>
                  <div className="hero-stat-label">Rated for 17+</div>
                </div>
              </div>

              <div className="hero-actions-row">
                <a
                  className="install-btn"
                  href={APK_URL}
                  target="_blank"
                  rel="noreferrer"
                  download
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                  </svg>
                  Install
                </a>
              </div>
            </div>

            <div className="hero-right-column">
              <>
                <img
                  src="/images/icindustries-icon.png"
                  alt="IC Industries"
                  className="hero-brand-icon"
                />
                <button className="trailer-btn" onClick={() => setIsTrailerOpen(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  Trailer
                </button>
              </>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="download-container">
        {/* Screenshots Section */}
        <div className="screenshots-wrapper">
          <div className="section-header">
            <h2>Screenshots</h2>
          </div>
          <div className="gallery">
            <button
              className="gallery-arrow gallery-arrow-left"
              onClick={goPrev}
              disabled={!canGoPrev}
              aria-label="Previous"
              type="button"
            >
              ‹
            </button>
            <div className="gallery-viewport">
              <div
                className="gallery-track"
                style={{
                  transform: `translateX(calc(-${galleryIndex} * (var(--gallery-item-width) + var(--gallery-gap))))`,
                }}
              >
                {galleryItems.map((item, idx) => (
                  <div className="gallery-item" key={idx}>
                    {item.type === 'video' ? (
                      <div className="gallery-video" role="button" tabIndex={0} onClick={() => setIsTrailerOpen(true)} onKeyDown={(e) => e.key === 'Enter' && setIsTrailerOpen(true)}>
                        <iframe
                          className="gallery-video-iframe"
                          src={item.src}
                          title={item.title}
                          frameBorder="0"
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                        <div className="gallery-video-overlay">
                          <span className="gallery-video-pill">Trailer</span>
                        </div>
                      </div>
                    ) : (
                      <img
                        className="gallery-image"
                        src={item.src}
                        alt={item.alt}
                        role="button"
                        tabIndex={0}
                        onClick={() => openImageModal(item.imageIndex)}
                        onKeyDown={(e) => e.key === 'Enter' && openImageModal(item.imageIndex)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <button
              className="gallery-arrow gallery-arrow-right"
              onClick={goNext}
              disabled={!canGoNext}
              aria-label="Next"
              type="button"
            >
              ›
            </button>
          </div>
        </div>

        {/* About This Game */}
        <div className="about-game">
          <div className="section-header">
            <h2>About this game</h2>
            <button className="arrow-btn">→</button>
          </div>
          <div className={showFullDescription ? 'description-full' : 'description-preview'}>
            <p>Step into a world where dreams and reality mix. Play as a boy trapped between waking life and a dream, exploring his memories each day. Each day uncovers a different part of his life, from school and family to love and loss. Your choices influence his journey: will he find hope and awaken, or remain lost in darkness?</p>
            
            {showFullDescription && (
              <>
                <h3 className="content-warning-title">Mature Content Description</h3>
                <p>This game contains content that may be distressing to some players such as drug use, tobacco use, traumatic experiences, abuse, suicide, anxiety, gore, loss of close ones, difficult parental relationships, attachment to past relationships, bullying, mentioned violence and bright flashing imagery.</p>
                
                <h3 className="installation-title">Installation Process</h3>
                <ol className="installation-steps">
                  <li>Open Settings &gt; Apps &gt; Special app access</li>
                  <li>Select your file manager or browser app</li>
                  <li>Toggle on Allow from this source</li>
                  <li>Open your file manager app</li>
                  <li>Navigate to the folder where you downloaded the APK</li>
                  <li>Tap the APK file</li>
                  <li>A prompt will appear asking if you want to install the app, tap Install</li>
                </ol>
              </>
            )}
          </div>
          {!showFullDescription && (
            <button 
              className="more-btn" 
              onClick={() => setShowFullDescription(true)}
            >
              More
            </button>
          )}
          <div className="tags">
            <span className="tag">Adventure</span>
            <span className="tag">Story-Driven</span>
            <span className="tag">Choices Matter</span>
          </div>
        </div>

        {/* App Info */}
        <div className="app-info-section">
          <div className="section-header">
            <h2>App info</h2>
          </div>
          <div className="info-rows">
            <div className="info-row">
              <span className="info-key">Requires Android</span>
              <span className="info-value">4.4 and up</span>
            </div>
            <div className="info-row">
              <span className="info-key">Size</span>
              <span className="info-value">1.8 GB</span>
            </div>
            <div className="info-row">
              <span className="info-key">Content rating</span>
              <span className="info-value">Rated for 17+ (Mature: Violence, Drug Use, Intense Themes)</span>
            </div>
            <div className="info-row">
              <span className="info-key">Developer</span>
              <span className="info-value">Daver The Dave<br/>daverthedave@gmail.com</span>
            </div>
          </div>
        </div>
      </div>

      {isTrailerOpen && (
        <div
          className="trailer-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Trailer"
          onClick={() => setIsTrailerOpen(false)}
        >
          <div className="trailer-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="trailer-modal-close"
              onClick={() => setIsTrailerOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="trailer-modal-body">
              <iframe
                className="trailer-modal-iframe"
                src="https://www.youtube.com/embed/BRBP1sdgyTE?autoplay=1&mute=1&controls=1&rel=0&modestbranding=1"
                title="7 Days Trailer"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {isImageOpen && (
        <div
          className="image-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot"
          onClick={closeImageModal}
        >
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={closeImageModal}
              aria-label="Close"
              type="button"
            >
              ×
            </button>

            <button
              className="image-modal-arrow image-modal-arrow-left"
              onClick={imagePrev}
              disabled={!canImagePrev}
              aria-label="Previous image"
              type="button"
            >
              ‹
            </button>
            <button
              className="image-modal-arrow image-modal-arrow-right"
              onClick={imageNext}
              disabled={!canImageNext}
              aria-label="Next image"
              type="button"
            >
              ›
            </button>

            <div className="image-modal-body">
              <img
                className="image-modal-img"
                src={imageItems[imageModalIndex]?.src}
                alt={imageItems[imageModalIndex]?.alt}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Download;
